// ClubDetailsStep Component
// Step 1: Basic club information

import React, { useState } from 'react';
import { 
  FaBuilding, 
  FaFutbol, 
  FaUsers, 
  FaBriefcase, 
  FaInfoCircle,
  FaTimes,
  FaPalette,
  FaQuoteLeft,
  FaGlobe,
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaYoutube,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import ClubLogoUpload from '../ClubLogoUpload';
import ClubBannerUpload from '../ClubBannerUpload';
import styles from './ClubDetailsStep.module.css';

// Predefined color options
const COLOR_PRESETS = [
  { name: 'Lime', value: '#A4EB3F' },
  { name: 'Red', value: '#DC2626' },
  { name: 'Blue', value: '#2563EB' },
  { name: 'Purple', value: '#7C3AED' },
  { name: 'Orange', value: '#EA580C' },
  { name: 'Teal', value: '#0D9488' },
  { name: 'Pink', value: '#DB2777' },
  { name: 'Gold', value: '#CA8A04' },
];

const ClubDetailsStep = ({ formData, updateFormData, sportsOptions, sportsData = [], sportsLoading = false, onNext, onClose }) => {
  const [showBranding, setShowBranding] = useState(false);
  const [showSocials, setShowSocials] = useState(false);
  
  // Helper to get emoji for a sport
  const getSportEmoji = (sportName) => {
    const sport = sportsData.find(s => s.name === sportName);
    return sport?.emoji || '🏆';
  };
  
  const handleChange = (field, value) => {
    updateFormData({ [field]: value });
  };

  const handleSocialLinkChange = (platform, value) => {
    updateFormData({
      socialLinks: {
        ...formData.socialLinks,
        [platform]: value || null
      }
    });
  };

  const isFormValid = () => {
    return formData.name.trim().length >= 3 && formData.sport !== '';
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h2 className={styles.title}>Club Details</h2>
          <p className={styles.subtitle}>Tell us about your club</p>
        </div>
        <button className={styles.closeButton} onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>
      </div>

      {/* Content */}
      <div className={styles.content}>
        <div className={styles.formLayout}>
          {/* Left Column - Logo Upload */}
          <div className={styles.logoSection}>
            <label className={styles.label}>Club Logo</label>
            <ClubLogoUpload
              value={formData.clubLogo}
              onChange={(logo) => handleChange('clubLogo', logo)}
            />
            <span className={styles.hint}>Optional</span>
          </div>

          {/* Right Column - Form Fields */}
          <div className={styles.fieldsSection}>
            {/* Club Name */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <FaBuilding className={styles.labelIcon} />
                Club Name <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                className={styles.input}
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter club name"
                maxLength={100}
              />
              {formData.name && formData.name.length < 3 && (
                <span className={styles.error}>Name must be at least 3 characters</span>
              )}
            </div>

            {/* Sport */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <FaFutbol className={styles.labelIcon} />
                Sport <span className={styles.required}>*</span>
              </label>
              {sportsLoading ? (
                <div className={styles.loadingText}>Loading sports...</div>
              ) : (
                <select
                  className={styles.select}
                  value={formData.sport}
                  onChange={(e) => handleChange('sport', e.target.value)}
                >
                  <option value="">Select a sport</option>
                  {sportsOptions.map(sport => (
                    <option key={sport} value={sport}>{getSportEmoji(sport)} {sport}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Type */}
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaUsers className={styles.labelIcon} />
                  Type
                </label>
                <select
                  className={styles.select}
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                >
                  <option value="Club">Club</option>
                  <option value="Team">Team</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaBriefcase className={styles.labelIcon} />
                  Business Type
                </label>
                <select
                  className={styles.select}
                  value={formData.businessType}
                  onChange={(e) => handleChange('businessType', e.target.value)}
                >
                  <option value="Public club">Public Club</option>
                  <option value="Private club">Private Club</option>
                  <option value="Registered business">Registered Business</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Description - Full Width */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            <FaInfoCircle className={styles.labelIcon} />
            Description
          </label>
          <textarea
            className={styles.textarea}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Tell potential members about your club..."
            rows={3}
            maxLength={500}
          />
          <span className={styles.charCount}>{formData.description.length}/500</span>
        </div>

        {/* Membership Settings */}
        <div className={styles.membershipSection}>
          <h3 className={styles.sectionTitle}>Membership</h3>
          
          <div className={styles.membershipOptions}>
            <label className={`${styles.membershipOption} ${formData.membershipType === 'free_open' ? styles.selected : ''}`}>
              <input
                type="radio"
                name="membershipType"
                value="free_open"
                checked={formData.membershipType === 'free_open'}
                onChange={(e) => handleChange('membershipType', e.target.value)}
              />
              <div className={styles.optionContent}>
                <span className={styles.optionTitle}>Free & Open</span>
              </div>
            </label>

            <label className={`${styles.membershipOption} ${formData.membershipType === 'free_approval' ? styles.selected : ''}`}>
              <input
                type="radio"
                name="membershipType"
                value="free_approval"
                checked={formData.membershipType === 'free_approval'}
                onChange={(e) => handleChange('membershipType', e.target.value)}
              />
              <div className={styles.optionContent}>
                <span className={styles.optionTitle}>Free + Approval</span>
              </div>
            </label>

            <label className={`${styles.membershipOption} ${formData.membershipType === 'paid' ? styles.selected : ''}`}>
              <input
                type="radio"
                name="membershipType"
                value="paid"
                checked={formData.membershipType === 'paid'}
                onChange={(e) => handleChange('membershipType', e.target.value)}
              />
              <div className={styles.optionContent}>
                <span className={styles.optionTitle}>Paid</span>
              </div>
            </label>

            {formData.membershipType === 'paid' && (
              <div className={styles.feeInputWrapper}>
                <span className={styles.currencySymbol}>$</span>
                <input
                  type="number"
                  className={styles.input}
                  value={formData.membershipFee}
                  onChange={(e) => handleChange('membershipFee', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  min="0"
                  step="1"
                />
                <span className={styles.feeLabel}>/year</span>
              </div>
            )}
          </div>
        </div>

        {/* Branding Section - Collapsible */}
        <div className={styles.collapsibleSection}>
          <button 
            type="button"
            className={styles.collapsibleHeader}
            onClick={() => setShowBranding(!showBranding)}
          >
            <div className={styles.collapsibleTitle}>
              <FaPalette className={styles.sectionIcon} />
              <span>Club Branding</span>
              <span className={styles.optionalBadge}>Optional</span>
            </div>
            {showBranding ? <FaChevronUp /> : <FaChevronDown />}
          </button>

          {showBranding && (
            <div className={styles.collapsibleContent}>
              {/* Banner Upload */}
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Hero Banner
                </label>
                <ClubBannerUpload
                  value={formData.bannerImage}
                  onChange={(banner) => handleChange('bannerImage', banner)}
                />
                <span className={styles.hint}>Displayed at top of your club page</span>
              </div>

              {/* Tagline */}
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaQuoteLeft className={styles.labelIcon} />
                  Tagline / Motto
                </label>
                <input
                  type="text"
                  className={styles.input}
                  value={formData.tagline || ''}
                  onChange={(e) => handleChange('tagline', e.target.value)}
                  placeholder="e.g., Building great players and great people"
                  maxLength={100}
                />
                <span className={styles.charCount}>{(formData.tagline || '').length}/100</span>
              </div>

              {/* Primary Color */}
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaPalette className={styles.labelIcon} />
                  Club Color
                </label>
                <div className={styles.colorPicker}>
                  {COLOR_PRESETS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      className={`${styles.colorSwatch} ${formData.primaryColor === color.value ? styles.selectedColor : ''}`}
                      style={{ backgroundColor: color.value }}
                      onClick={() => handleChange('primaryColor', color.value)}
                      title={color.name}
                    />
                  ))}
                  <div className={styles.customColorWrapper}>
                    <input
                      type="color"
                      className={styles.customColorInput}
                      value={formData.primaryColor || '#A4EB3F'}
                      onChange={(e) => handleChange('primaryColor', e.target.value)}
                      title="Custom color"
                    />
                  </div>
                </div>
                <span className={styles.hint}>Applied to accents on your club page</span>
              </div>
            </div>
          )}
        </div>

        {/* Social Links Section - Collapsible */}
        <div className={styles.collapsibleSection}>
          <button 
            type="button"
            className={styles.collapsibleHeader}
            onClick={() => setShowSocials(!showSocials)}
          >
            <div className={styles.collapsibleTitle}>
              <FaGlobe className={styles.sectionIcon} />
              <span>Social Links</span>
              <span className={styles.optionalBadge}>Optional</span>
            </div>
            {showSocials ? <FaChevronUp /> : <FaChevronDown />}
          </button>

          {showSocials && (
            <div className={styles.collapsibleContent}>
              <div className={styles.socialLinksGrid}>
                {/* Website */}
                <div className={styles.socialInputGroup}>
                  <FaGlobe className={styles.socialIcon} />
                  <input
                    type="url"
                    className={styles.socialInput}
                    value={formData.socialLinks?.website || ''}
                    onChange={(e) => handleSocialLinkChange('website', e.target.value)}
                    placeholder="https://yourclub.com"
                  />
                </div>

                {/* Facebook */}
                <div className={styles.socialInputGroup}>
                  <FaFacebook className={styles.socialIcon} style={{ color: '#1877F2' }} />
                  <input
                    type="url"
                    className={styles.socialInput}
                    value={formData.socialLinks?.facebook || ''}
                    onChange={(e) => handleSocialLinkChange('facebook', e.target.value)}
                    placeholder="https://facebook.com/yourclub"
                  />
                </div>

                {/* Instagram */}
                <div className={styles.socialInputGroup}>
                  <FaInstagram className={styles.socialIcon} style={{ color: '#E4405F' }} />
                  <input
                    type="url"
                    className={styles.socialInput}
                    value={formData.socialLinks?.instagram || ''}
                    onChange={(e) => handleSocialLinkChange('instagram', e.target.value)}
                    placeholder="https://instagram.com/yourclub"
                  />
                </div>

                {/* Twitter */}
                <div className={styles.socialInputGroup}>
                  <FaTwitter className={styles.socialIcon} style={{ color: '#1DA1F2' }} />
                  <input
                    type="url"
                    className={styles.socialInput}
                    value={formData.socialLinks?.twitter || ''}
                    onChange={(e) => handleSocialLinkChange('twitter', e.target.value)}
                    placeholder="https://twitter.com/yourclub"
                  />
                </div>

                {/* YouTube */}
                <div className={styles.socialInputGroup}>
                  <FaYoutube className={styles.socialIcon} style={{ color: '#FF0000' }} />
                  <input
                    type="url"
                    className={styles.socialInput}
                    value={formData.socialLinks?.youtube || ''}
                    onChange={(e) => handleSocialLinkChange('youtube', e.target.value)}
                    placeholder="https://youtube.com/@yourclub"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <button
          className={styles.cancelButton}
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          className={styles.nextButton}
          onClick={onNext}
          disabled={!isFormValid()}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ClubDetailsStep;

